import React from 'react';

import { DateTime } from 'luxon';

import { client } from '@admin/libraries/apollo';
import { useRegionsSelectorQuery } from '@admin/schema';

import { dateInputBounds, formatDateInputValue, isMode, parseDateInputValue, usersForRegion } from './filters_util';
import { IAvailabilityFilters, IAvailabilityUser } from './types';

type FiltersPanelProps = {
  filters: IAvailabilityFilters;
  users: IAvailabilityUser[];
  onChange(filters: IAvailabilityFilters): void;
};

const titleize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const FiltersPanel: React.FC<FiltersPanelProps> = ({ filters, users, onChange }) => {
  const { data: regionData } = useRegionsSelectorQuery({ client });
  const regions = regionData?.regions ?? [];
  const filteredUsers = usersForRegion(users, filters.region_id);
  const { min, max } = dateInputBounds();

  const updateFilters = (next: Partial<IAvailabilityFilters>) => {
    onChange({ ...filters, ...next });
  };

  return (
    <div className="panel">
      <div className="panel-body">
        <form className="form-inline">
          <div className="form-grouping">
            <div className="form-group">
              <select
                className="form-control"
                value={filters.region_id ?? ''}
                onChange={(event) => {
                  const regionID = event.target.value ? Number(event.target.value) : undefined;
                  updateFilters({ region_id: regionID });
                }}
              >
                {!filters.region_id && <option value=""> - Region - </option>}
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-grouping">
            <div className="form-group">
              <label className="radio">
                <input
                  name="filters[mode]"
                  type="radio"
                  checked={filters.mode === 'daily'}
                  onChange={() => updateFilters({ mode: 'daily' })}
                />{' '}
                Daily View
              </label>
            </div>

            <div className="form-group">
              <input
                name="filters[weekly][date]"
                type="date"
                className="form-control form-control-fit"
                min={min}
                max={max}
                value={formatDateInputValue(filters.date)}
                disabled={filters.mode !== 'daily'}
                onChange={(event) => updateFilters({ date: parseDateInputValue(event.target.value) })}
              />
            </div>
          </div>

          <div className="form-grouping">
            <div className="form-group">
              <label className="radio">
                <input
                  name="filters[mode]"
                  type="radio"
                  checked={filters.mode === 'management'}
                  onChange={() => updateFilters({ mode: 'management' })}
                />{' '}
                Specific User
              </label>
            </div>

            <div className="form-group">
              <select
                className="form-control"
                value={filters.user_id ?? ''}
                disabled={filters.mode !== 'management'}
                onChange={(event) => {
                  const userID = event.target.value ? Number(event.target.value) : undefined;
                  updateFilters({ user_id: userID });
                }}
              >
                <option value=""> - User - </option>
                {filteredUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export const formatHourLabel = (time: Date) => DateTime.fromJSDate(time, { zone: 'utc' }).toFormat('h a');

export const formatDisplayDate = (value: string | null) => {
  if (!value) {
    return null;
  }
  return DateTime.fromISO(value, { zone: 'utc' }).toFormat('EEE, MMM d, yyyy');
};

export { titleize, isMode };
